import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import {
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSpinner,
  IonToolbar,
  useIonRouter,
} from '@ionic/react';
import {
  alertCircle,
  alertCircleOutline,
  barChartOutline,
  calculatorOutline,
  checkmarkOutline,
  chevronBackOutline,
  cloudDownloadOutline,
  calendarOutline,
  callOutline,
  cashOutline,
  chatbubblesOutline,
  chevronForwardOutline,
  clipboardOutline,
  closeOutline,
  constructOutline,
  createOutline,
  hourglassOutline,
  notificationsOutline,
  pencil,
  peopleCircleOutline,
  peopleOutline,
  personOutline,
  receiptOutline,
  settingsOutline,
  trendingUpOutline,
  warningOutline,
} from 'ionicons/icons';
import { useAuth } from '../../../../contexts';
import { AuthContext } from '../../../../contexts/auth-context-core';
import { openIvrDialer } from '../../../components/IvrPanel/ivrPanelBus';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import type { Shortcut } from '@rsmaids/shared/types/dashboard';
import { getBookingsFromCache, subscribeToUpcomingBookings } from '@rsmaids/shared/services/api/bookingsCacheService';
import { contactsCache } from '@rsmaids/shared/services/api/contactsCacheService';
import type { BookingItem } from '@rsmaids/shared/types/bookings';
import { getServiceColor, type ServiceType } from '@rsmaids/shared/types/services';
import type { Activity } from '@rsmaids/shared/types/analytics';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { getNotificationsViaFunction } from '../../../../utils/notificationUtils';
import { getZohoInvoices, getZohoProfitAndLossIncome, type ZohoInvoice } from '../../../../services/zohoInvoicingService';
import CreateBookingDesktop from '../../bookings/CreateBookingDesktop';
import styles from './DashboardOwnersDesktop.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import { translateDynamic } from '../../../../i18n/dynamic';
import { intlLocale } from '../../../../i18n/locale';
import { localizeActivity } from '../../../../utils/activityI18n';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function toDate(input: Date | string | undefined): Date | null {
  if (!input) return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  const value = String(input).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getDateParts(booking: BookingItem): { day: string; month: string; weekday: string } {
  const dt = toDate(booking.startTime);
  if (dt) {
    return {
      day: String(dt.getDate()),
      month: dt.toLocaleDateString(intlLocale(), { month: 'short' }),
      weekday: dt.toLocaleDateString(intlLocale(), { weekday: 'short' }),
    };
  }
  // Fall back to the YYYY-MM-DD date string, parsed as local (not UTC)
  if (booking.date) {
    const [year, month, day] = booking.date.split('T')[0].split('-').map(Number);
    if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
      const local = new Date(year, month - 1, day);
      return {
        day: String(day),
        month: local.toLocaleDateString(intlLocale(), { month: 'short' }),
        weekday: local.toLocaleDateString(intlLocale(), { weekday: 'short' }),
      };
    }
  }
  return { day: '', month: '', weekday: '' };
}

function bookingTimeOnly(startTime: Date | string | undefined): string {
  const dt = toDate(startTime);
  if (!dt) return 'Time unavailable';
  return dt.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatDuration(duration: number | undefined): string {
  if (!duration || duration <= 0) return '1h';
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

function normalizedStatus(status: unknown): string {
  return String(status || '').trim().toLowerCase();
}

function isCancelledLikeStatus(status: unknown): boolean {
  const s = normalizedStatus(status);
  return s === 'cancelled' || s === 'canceled' || s === 'refunded' || s === 'void' || s === 'voided';
}

function isCompletedLikeStatus(status: unknown): boolean {
  const s = normalizedStatus(status);
  return s === 'completed' || s === 'done';
}

function statusRank(status: unknown): number {
  const s = normalizedStatus(status);
  if (s === 'confirmed' || s === 'booked' || s === 'rescheduled') return 4;
  if (s === 'pending' || s === 'due' || s === 'payment_awaiting') return 3;
  if (isCompletedLikeStatus(s)) return 2;
  if (isCancelledLikeStatus(s)) return 1;
  return 0;
}

function bookingIdentityKey(item: BookingItem): string {
  const startMs = toDate(item.startTime)?.getTime() ?? 0;
  const service = String(item.serviceType || item.title || '').trim().toLowerCase();
  const contact = String(item.contactId || item.contact?.id || item.contact?.name || '').trim().toLowerCase();
  const location = String(item.location || item.contact?.address || '').trim().toLowerCase();
  return `${contact}|${startMs}|${service}|${location}`;
}

function normalizeUpcomingBookings(items: BookingItem[], now: Date): BookingItem[] {
  const upcoming = items.filter(b => {
    if (isCancelledLikeStatus(b.status) || isCompletedLikeStatus(b.status)) return false;
    const startsAt = toDate(b.startTime);
    return startsAt ? startsAt.getTime() >= now.getTime() : false;
  });

  upcoming.sort((a, b) => {
    const aTime = toDate(a.startTime)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bTime = toDate(b.startTime)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });

  return upcoming;
}

function dedupeBookingsById(items: BookingItem[]): BookingItem[] {
  const byId = new Map<string, BookingItem>();
  const bySlotWithoutId = new Map<string, BookingItem>();

  items.forEach((item) => {
    if (!item) return;

    const id = String(item.id || '').trim();
    if (id) {
      const currentById = byId.get(id);
      if (!currentById || statusRank(item.status) >= statusRank(currentById.status)) {
        byId.set(id, item);
      }
      return;
    }

    const slotKey = bookingIdentityKey(item);
    const currentBySlot = bySlotWithoutId.get(slotKey);
    if (!currentBySlot || statusRank(item.status) >= statusRank(currentBySlot.status)) {
      bySlotWithoutId.set(slotKey, item);
    }
  });

  return [...Array.from(byId.values()), ...Array.from(bySlotWithoutId.values())];
}

function invoiceStatus(inv: ZohoInvoice): string {
  return String(inv.status || '').toLowerCase();
}

function formatActivityTime(input: Date | string | undefined): string {
  const date = toDate(input);
  if (!date) return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.recently');
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 2) return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.justNow');
  if (diffMins < 60) return i18n.t('activity.minutesAgo', { n: diffMins });
  if (diffHours < 24) return i18n.t('activity.hoursAgo', { n: diffHours });
  if (diffDays === 1) return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.yesterday');
  if (diffDays < 7) return i18n.t('activity.daysAgo', { n: diffDays });
  return date.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' });
}

function zohoInvoiceToActivity(inv: ZohoInvoice): Activity {
  const clientName = inv.customer_name || 'Client';
  const amount = inv.total ? `${Number(inv.total).toFixed(2)} USD` : '';
  const invoiceNum = inv.invoice_number ? `Invoice #${inv.invoice_number}` : '';
  const userInitial = clientName.charAt(0).toUpperCase();
  const status = invoiceStatus(inv);
  const time = formatActivityTime(inv.last_modified_time || inv.created_time || inv.date);

  if (status === 'paid') {
    return {
      id: `zoho-paid-${inv.invoice_id}`,
      type: 'payment',
      title: 'Payment Received',
      detail: [invoiceNum, amount ? `for ${amount}` : '', clientName ? `paid by ${clientName}` : ''].filter(Boolean).join(' '),
      time,
      userInitial,
    };
  }
  if (status === 'overdue') {
    return {
      id: `zoho-overdue-${inv.invoice_id}`,
      type: 'overdue',
      title: 'Payment Overdue',
      detail: [invoiceNum, amount ? `for ${amount}` : '', clientName ? `— ${clientName}` : ''].filter(Boolean).join(' '),
      time,
      userInitial,
    };
  }
  return {
    id: `zoho-invoice-${inv.invoice_id}`,
    type: 'invoice',
    title: 'Invoice Sent',
    detail: [invoiceNum, amount ? `for ${amount}` : '', clientName ? `— ${clientName}` : ''].filter(Boolean).join(' '),
    time,
    userInitial,
  };
}

// Same icon mapping as the mobile dashboard's getActivityTypeIcon
function getActivityTypeIcon(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('payment') || t.includes('invoice') || t.includes('cash')) return receiptOutline;
  if (t.includes('overdue') || t.includes('due')) return alertCircle;
  return calendarOutline;
}

const BUBBLE_COLORS = [
  'var(--ion-color-primary)',
  'var(--ion-color-secondary)',
  'var(--ion-color-tertiary)',
  'var(--ion-color-success)',
  'var(--ion-color-warning)',
];

function getBubbleColor(initial?: string): string {
  const code = (initial || 'A').toUpperCase().charCodeAt(0);
  return BUBBLE_COLORS[code % BUBBLE_COLORS.length];
}

function getCategoryLabel(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('payment') || t.includes('invoice') || t.includes('overdue')) return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.financial');
  return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.generalUpdates');
}

// Two-level grouping: broad category -> fine-grained title (mirrors mobile)
function groupActivitiesByCategory(activities: Activity[]): Record<string, Record<string, Activity[]>> {
  const result: Record<string, Record<string, Activity[]>> = {};
  activities.forEach(activity => {
    const category = getCategoryLabel(activity.title);
    if (!result[category]) result[category] = {};
    if (!result[category][activity.title]) result[category][activity.title] = [];
    result[category][activity.title].push(activity);
  });
  return result;
}

function activityRoute(activity: Activity): string {
  if (activity.id.startsWith('zoho-')) return '/tabs/billing';
  const bookingId = activity.id.replace(/^(cancel|complete|overdue|confirm|booking)-/, '');
  return `/tabs/bookings/${bookingId}`;
}

function bookingBadgeClass(status: BookingItem['status']): string {
  const s = normalizedStatus(status);
  if (s === 'confirmed' || s === 'completed' || s === 'booked' || s === 'rescheduled') return 'badgeSuccess';
  if (s === 'cancelled' || s === 'canceled' || s === 'overdue' || s === 'refunded' || s === 'void' || s === 'voided') return 'badgeDanger';
  if (s === 'pending' || s === 'due' || s === 'payment_awaiting') return 'badgeWarning';
  return 'badgeMedium';
}

function formatBookingStatusLabel(status: BookingItem['status']): string {
  const s = normalizedStatus(status).replace(/_/g, ' ').replace(/-/g, ' ');
  if (!s) return '';
  return s.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

const ALL_SHORTCUTS: Shortcut[] = [
  // Core shortcuts
  { id: 'calendar', icon: calendarOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.schedule'); }, route: '/tabs/schedule', category: 'core' },
  { id: 'contacts', icon: peopleOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.contacts'); }, route: '/tabs/contacts', category: 'core' },
  { id: 'bookings', icon: clipboardOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.bookings'); }, route: '/tabs/bookings', category: 'core' },

  // Business shortcuts
  { id: 'payments', icon: cashOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.payments'); }, route: '/tabs/payments', category: 'business' },
  { id: 'invoices', icon: receiptOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.invoices'); }, route: '/tabs/invoice-list-desktop', category: 'business' },
  { id: 'estimates', icon: calculatorOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.estimates'); }, route: '/estimates', category: 'business' },
  { id: 'services', icon: constructOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.services'); }, route: '/tabs/services', category: 'business' },

  // Communication shortcuts
  { id: 'messages', icon: chatbubblesOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.messages'); }, route: '/tabs/inbox', category: 'communication' },
  { id: 'alerts', icon: notificationsOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.alerts'); }, route: '/tabs/notifications', category: 'communication' },

  // Analytics shortcuts
  { id: 'analytics', icon: trendingUpOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.analytics'); }, route: '/tabs/business-analytics', category: 'analytics' },
  { id: 'reports', icon: barChartOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.reports'); }, route: '/reports', category: 'analytics' },

  // Admin shortcuts
  { id: 'profile', icon: personOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.profile'); }, route: '/tabs/profile', category: 'admin' },
  { id: 'settings', icon: settingsOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.settings'); }, route: '/tabs/settings', category: 'admin' },
  { id: 'team', icon: peopleCircleOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.team'); }, route: '/tabs/team-members', category: 'admin' },
  { id: 'backup', icon: cloudDownloadOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.backup'); }, route: '/backup', category: 'admin' },
  { id: 'notifications', icon: notificationsOutline, get title() { return i18n.t('desktop.pages.dashboard.owner.DashboardOwnersDesktop.notifications'); }, route: '/tabs/employeeManagement?section=notifications', category: 'admin' },
];

const DashboardOwnersDesktop: React.FC = () => {
  const { t: tr } = useTranslation();
  const router = useIonRouter();
  const { authState } = useAuth();
  const { user } = authState;
  const authContext = useContext(AuthContext);

  const [showCreateBooking, setShowCreateBooking] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [upcomingBookings, setUpcomingBookings] = useState<BookingItem[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsPage, setBookingsPage] = useState(0);
  const [invoices, setInvoices] = useState<ZohoInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [monthRevenue, setMonthRevenue] = useState<number | null>(null);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [bookingActivities, setBookingActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [draftShortcutIds, setDraftShortcutIds] = useState<string[]>([]);
  const [isSavingShortcuts, setIsSavingShortcuts] = useState(false);
  const shortcutsSwiperRef = useRef<SwiperType | null>(null);

  const [activeShortcutIds, setActiveShortcutIds] = useState<string[]>(
    user?.preferences?.dashboardShortcuts || ['leads', 'analytics', 'profile', 'alerts', 'settings']
  );

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) return;
      try {
        const result = await getNotificationsViaFunction(user.id);
        const notifications = (result?.notifications || []) as Array<{ read?: boolean }>;
        setUnreadNotificationsCount(notifications.filter(n => !n.read).length);
      } catch {
        // silent fallback for dashboard polling
      }
    };

    loadNotifications();
    const timer = setInterval(loadNotifications, 30000);
    return () => clearInterval(timer);
  }, [user?.id]);

  // Sync from user.preferences.dashboardShortcuts whenever it changes —
  // computed during render (React's "adjust state when a prop changes"
  // pattern) rather than as a setState call inside an effect.
  const [prevDashboardShortcuts, setPrevDashboardShortcuts] = useState(user?.preferences?.dashboardShortcuts);
  if (prevDashboardShortcuts !== user?.preferences?.dashboardShortcuts) {
    setPrevDashboardShortcuts(user?.preferences?.dashboardShortcuts);
    if (user?.preferences?.dashboardShortcuts?.length) {
      setActiveShortcutIds(user.preferences.dashboardShortcuts);
    }
  }

  useEffect(() => {
    // Don't load anything until auth is fully loaded and user preferences are available
    if (!user || authState.isLoading) return;

    // Wait a bit more to ensure preferences have been loaded from server
    const timer = setTimeout(() => {
      // Check if user has saved preferences first
      if (user.preferences?.dashboardShortcuts && user.preferences.dashboardShortcuts.length > 0) {
        setActiveShortcutIds(user.preferences.dashboardShortcuts);
      } else {
        // Check localStorage as secondary fallback
        const localShortcuts = localStorage.getItem('dashboard-owner-shortcuts');
        if (localShortcuts) {
          try {
            const parsedShortcuts = JSON.parse(localShortcuts);
            if (Array.isArray(parsedShortcuts) && parsedShortcuts.length > 0) {
              setActiveShortcutIds(parsedShortcuts);
              return;
            }
          } catch {
            // ignore parse issues and fall through to defaults
          }
        }
        // Only set defaults if no preferences exist anywhere
        setActiveShortcutIds(['leads', 'analytics', 'profile', 'alerts', 'settings']);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [user, authState.isLoading]);

  const loadUpcomingBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const now = new Date();
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      const end = new Date(today);
      end.setDate(end.getDate() + 7);
      end.setHours(23, 59, 59, 999);

      const [cachedResult, apiResult] = await Promise.allSettled([
        getBookingsFromCache(today, end),
        DataServiceFactory.getService().bookings.getBookingsByDateRange(today, end),
      ]);

      const cachedList = cachedResult.status === 'fulfilled' ? cachedResult.value : [];
      const apiList = apiResult.status === 'fulfilled' ? apiResult.value : [];
      const merged = dedupeBookingsById([...(cachedList || []), ...((apiList as BookingItem[]) || [])]);
      const upcoming = normalizeUpcomingBookings(merged, now);

      setUpcomingBookings(upcoming);
      setBookingsPage(0);
    } catch {
      setUpcomingBookings([]);
      setBookingsPage(0);
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const fetched = await getZohoInvoices({ page: 1 });
      setInvoices(fetched);
    } catch {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  const loadActivities = useCallback(async () => {
    setActivitiesLoading(true);
    try {
      const fetched = await DataServiceFactory.getService().analytics.getRecentActivities();
      setBookingActivities(fetched);
    } catch {
      setBookingActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, []);

  const loadRevenue = useCallback(async () => {
    setRevenueLoading(true);
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      const income = await getZohoProfitAndLossIncome({ date_start: monthStart, date_end: monthEnd });
      setMonthRevenue(income);
    } catch {
      setMonthRevenue(null);
    } finally {
      setRevenueLoading(false);
    }
  }, []);

  useEffect(() => {
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body).
    Promise.resolve().then(() => {
      loadUpcomingBookings();
      loadInvoices();
      loadActivities();
      loadRevenue();
    });
  }, [loadUpcomingBookings, loadInvoices, loadActivities, loadRevenue]);

  // Keep upcoming bookings live (cache + server sync + any booking create/update/reschedule/cancel).
  useEffect(() => {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setDate(end.getDate() + 7);
    end.setHours(23, 59, 59, 999);

    const unsubscribe = subscribeToUpcomingBookings(
      today,
      end,
      (items) => {
        const next = normalizeUpcomingBookings(items, new Date());
        setUpcomingBookings(next);
        setBookingsLoading(false);
        setBookingsPage(prev => {
          const maxPage = Math.max(0, Math.ceil(next.length / 6) - 1);
          return Math.min(prev, maxPage);
        });

        // Firestore query can miss edge-case docs when source field shapes/types drift;
        // merge in API range results as a second pass to keep dashboard complete.
        void (async () => {
          try {
            const apiList = await DataServiceFactory.getService().bookings.getBookingsByDateRange(today, end);
            const merged = dedupeBookingsById([...(items || []), ...((apiList as BookingItem[]) || [])]);
            const mergedUpcoming = normalizeUpcomingBookings(merged, new Date());
            setUpcomingBookings(mergedUpcoming);
            setBookingsPage(prev => {
              const maxPage = Math.max(0, Math.ceil(mergedUpcoming.length / 6) - 1);
              return Math.min(prev, maxPage);
            });
          } catch {
            // Keep snapshot state if API merge fails.
          }
        })();
      }
    );

    return unsubscribe;
  }, []);

  // Booking activities (new/confirmed/cancelled/completed) interleaved with
  // invoice activities (payment received/overdue/sent), like the mobile dashboard
  const recentActivities = useMemo(() => {
    const invoiceActivities = invoices.map(zohoInvoiceToActivity);
    return [...bookingActivities, ...invoiceActivities];
  }, [bookingActivities, invoices]);

  const groupedActivities = useMemo(
    () => groupActivitiesByCategory(recentActivities),
    [recentActivities]
  );

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = useCallback((title: string) => {
    setExpandedGroups(prev => ({ ...prev, [title]: !prev[title] }));
  }, []);

  const allShortcuts = ALL_SHORTCUTS;

  const categoryNames = {
    core: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.coreFeatures'),
    business: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.businessTools'),
    communication: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.communication'),
    analytics: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.analyticsReports'),
    admin: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.administration'),
  };

  const normalizedShortcutIds = useMemo(() => {
    return Array.from(new Set(activeShortcutIds));
  }, [activeShortcutIds]);

  const activeShortcuts = useMemo(
    () => normalizedShortcutIds
      .map(id => allShortcuts.find(shortcut => shortcut.id === id))
      .filter((shortcut): shortcut is Shortcut => Boolean(shortcut)),
    [normalizedShortcutIds, allShortcuts]
  );

  const categorizedShortcuts = Object.entries(categoryNames)
    .map(([categoryId, categoryLabel]) => ({
      id: categoryId,
      label: categoryLabel,
      shortcuts: allShortcuts.filter(shortcut => shortcut.category === categoryId),
    }))
    .filter(category => category.shortcuts.length > 0);

  const openShortcutEditor = useCallback(() => {
    setDraftShortcutIds(activeShortcutIds);
    setIsEditMode(true);
  }, [activeShortcutIds]);

  const scrollQuickActionsPrev = useCallback(() => {
    shortcutsSwiperRef.current?.slidePrev(220);
  }, []);

  const scrollQuickActionsNext = useCallback(() => {
    shortcutsSwiperRef.current?.slideNext(220);
  }, []);

  const closeShortcutEditor = useCallback(() => {
    setIsEditMode(false);
    setDraftShortcutIds([]);
  }, []);

  const toggleDraftShortcut = useCallback((shortcutId: string) => {
    setDraftShortcutIds((prev) => {
      if (prev.includes(shortcutId)) {
        return prev.filter((id) => id !== shortcutId);
      }
      return [...prev, shortcutId];
    });
  }, []);

  const saveShortcutsToStorage = useCallback(async (shortcuts: string[]) => {
    try {
      await DataServiceFactory.getService().auth.updateUserPreferences({ dashboardShortcuts: shortcuts });
      authContext?.actions?.updateLocalPreferences({ dashboardShortcuts: shortcuts });
      localStorage.setItem('dashboard-owner-shortcuts', JSON.stringify(shortcuts));
    } catch {
      localStorage.setItem('dashboard-owner-shortcuts', JSON.stringify(shortcuts));
    }
  }, [authContext]);

  const saveShortcutEditor = useCallback(async () => {
    setActiveShortcutIds(draftShortcutIds);
    setIsSavingShortcuts(true);
    try {
      await saveShortcutsToStorage(draftShortcutIds);
    } finally {
      setIsSavingShortcuts(false);
    }
    closeShortcutEditor();
  }, [closeShortcutEditor, draftShortcutIds, saveShortcutsToStorage]);

  const BOOKINGS_PER_PAGE = 6;
  const bookingsPageCount = Math.max(1, Math.ceil(upcomingBookings.length / BOOKINGS_PER_PAGE));
  const displayBookings = useMemo(
    () => upcomingBookings.slice(bookingsPage * BOOKINGS_PER_PAGE, (bookingsPage + 1) * BOOKINGS_PER_PAGE),
    [upcomingBookings, bookingsPage]
  );

  // Collected this month = Zoho's own Profit & Loss report (see loadRevenue) —
  // already nets out voided invoices, refunds, and credit notes correctly,
  // unlike summing invoice totals ourselves (which also only sees page 1 / 25
  // most-recent invoices and can silently undercount).
  const paidThisMonth = monthRevenue ?? 0;

  const overdueInvoices = useMemo(
    () => invoices.filter(inv => invoiceStatus(inv) === 'overdue'),
    [invoices]
  );

  const overdueAmount = useMemo(
    () => overdueInvoices.reduce((sum, inv) => sum + (inv.balance ?? inv.total ?? 0), 0),
    [overdueInvoices]
  );

  const outstandingAmount = useMemo(
    () => invoices
      .filter(inv => !['paid', 'void', 'draft', 'overdue'].includes(invoiceStatus(inv)))
      .reduce((sum, inv) => sum + (inv.balance ?? inv.total ?? 0), 0),
    [invoices]
  );

  const paidInvoiceCount = useMemo(
    () => invoices.filter(inv => invoiceStatus(inv) === 'paid').length,
    [invoices]
  );

  const pendingCount = useMemo(
    () => upcomingBookings.filter(booking => booking.status === 'pending').length,
    [upcomingBookings]
  );

  const confirmedCount = useMemo(
    () => upcomingBookings.filter(booking => booking.status === 'confirmed').length,
    [upcomingBookings]
  );

  const revenueSplit = useMemo(() => {
    const total = paidThisMonth + outstandingAmount + overdueAmount;
    if (total <= 0) return null;
    return {
      paid: (paidThisMonth / total) * 100,
      due: (outstandingAmount / total) * 100,
      overdue: (overdueAmount / total) * 100,
    };
  }, [paidThisMonth, outstandingAmount, overdueAmount]);

  const kpis = [
    {
      icon: calendarOutline,
      accent: styles.accentPrimary,
      label: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.upcomingBookings'),
      value: bookingsLoading ? '—' : String(upcomingBookings.length),
      hint: 'Next 7 days',
      route: '/tabs/bookings',
    },
    {
      icon: hourglassOutline,
      accent: styles.accentWarning,
      label: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.pending'),
      value: bookingsLoading ? '—' : String(pendingCount),
      hint: 'Awaiting confirmation',
      route: '/tabs/bookings',
    },
    {
      icon: cashOutline,
      accent: styles.accentSuccess,
      label: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.collected'),
      value: revenueLoading ? '—' : (monthRevenue === null ? '—' : formatCurrency(monthRevenue)),
      hint: 'Paid this month',
      route: '/tabs/billing',
    },
    {
      icon: alertCircleOutline,
      accent: styles.accentDanger,
      label: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.overdue'),
      value: invoicesLoading ? '—' : formatCurrency(overdueAmount),
      hint: invoicesLoading
        ? 'Invoices past due'
        : `${overdueInvoices.length} invoice${overdueInvoices.length === 1 ? '' : 's'} past due`,
      route: '/tabs/billing',
    },
  ];

  const renderBookingAvatar = (booking: BookingItem) => {
    const name = booking.contact?.name || 'Client';
    const cached = booking.contactId ? contactsCache.getAvatar(booking.contactId) : undefined;
    const url = cached || booking.contact?.avatarUrl;
    const src = url && url.trim() !== '' ? proxyImage(url) : getDefaultAvatar(name);
    return (
      <img
        src={src}
        alt={name}
        className={styles.bookingAvatar}
        onError={e => { (e.target as HTMLImageElement).src = getDefaultAvatar(name); }}
      />
    );
  };

  const weekStats = [
    { label: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.scheduledBookings'), value: bookingsLoading ? '—' : String(upcomingBookings.length) },
    { label: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.confirmed'), value: bookingsLoading ? '—' : String(confirmedCount) },
    { label: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.awaitingConfirmation'), value: bookingsLoading ? '—' : String(pendingCount) },
    { label: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.recentInvoices'), value: invoicesLoading ? '—' : String(invoices.length) },
    { label: tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.paidInvoices'), value: invoicesLoading ? '—' : String(paidInvoiceCount) },
  ];

  return (
    <IonPage className={styles.page}>
      <IonHeader className={styles.header}>
        <IonToolbar className={styles.toolbar}>
          <div className={styles.topBarLeft} slot="start">
            <img src="/maskable_icon_x512.png" alt={tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.rsmaidsLogo')} className={styles.companyLogo} />
            <span className={styles.companyName}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.rSMaidsService')}</span>
          </div>
          <div className={styles.topBarRight} slot="end">
            <IonButton fill="outline" size="small" className={styles.actionButton} onClick={() => openIvrDialer()}>
              <IonIcon icon={callOutline} slot="start" />{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.quickCall')}
            </IonButton>
            <IonButton fill="solid" size="small" className={styles.primaryButton} onClick={() => setShowCreateBooking(true)}>
              <IonIcon icon={createOutline} slot="start" />{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.newBooking')}
            </IonButton>
            <div className={styles.notifWrap}>
              <IonButton fill="clear" className={styles.iconButton} onClick={() => router.push('/tabs/notifications')}>
                <div className={styles.iconWrap}>
                  <IonIcon icon={notificationsOutline} />
                </div>
              </IonButton>
              {unreadNotificationsCount > 0 && (
                <span className={styles.notifBadge}>
                  {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                </span>
              )}
            </div>
            <IonButton fill="clear" className={styles.iconButton} onClick={() => router.push('/team/messages')}>
              <IonIcon icon={chatbubblesOutline} />
            </IonButton>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent className={styles.pageContent} scrollY={false}>
        <div className={styles.pageScroll}>
          <div className={styles.body}>

            {/* Quick Access shortcuts — top section under header */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.quickAccess')}</span>
                <IonButton fill="clear" size="small" className={styles.panelAction}
                  onClick={openShortcutEditor}>
                  <IonIcon icon={pencil} slot="icon-only" />
                </IonButton>
              </div>
              <div className={styles.shortcutsRow}>
                <IonButton
                  fill="clear"
                  className={styles.shortcutNavButton}
                  onClick={scrollQuickActionsPrev}
                  aria-label={tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.scrollQuickActionsLeft')}
                >
                  <IonIcon icon={chevronBackOutline} />
                </IonButton>

                <Swiper
                  modules={[FreeMode, Mousewheel]}
                  slidesPerView="auto"
                  spaceBetween={8}
                  watchOverflow={false}
                  centeredSlides={false}
                  slidesOffsetBefore={4}
                  slidesOffsetAfter={96}
                  resistanceRatio={0.35}
                  freeMode={{ enabled: true, momentum: true, momentumRatio: 0.35 }}
                  observer={true}
                  observeParents={true}
                  allowTouchMove={true}
                  simulateTouch={true}
                  grabCursor={true}
                  touchStartPreventDefault={false}
                  mousewheel={{ forceToAxis: false, releaseOnEdges: true, sensitivity: 0.9 }}
                  touchEventsTarget="container"
                  className={styles.shortcutsGrid}
                  onSwiper={(swiper) => {
                    shortcutsSwiperRef.current = swiper;
                  }}
                >
                  <SwiperSlide className={styles.shortcutSlide}>
                    <div className={`${styles.shortcutItem} ${styles.shortcutCall}`}
                      onClick={() => openIvrDialer()}
                      role="button" tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && openIvrDialer()}
                    >
                      <IonIcon icon={callOutline} className={styles.shortcutIcon} />
                      <span className={styles.shortcutLabel}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.call')}</span>
                    </div>
                  </SwiperSlide>

                  {activeShortcuts.map(s => (
                    <SwiperSlide key={s.id} className={styles.shortcutSlide}>
                      <div className={styles.shortcutItem}
                        onClick={() => router.push(s.route)}
                        role="button" tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && router.push(s.route)}
                      >
                        <IonIcon icon={s.icon} className={styles.shortcutIcon} />
                        <span className={styles.shortcutLabel}>{s.title}</span>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                <IonButton
                  fill="clear"
                  className={styles.shortcutNavButton}
                  onClick={scrollQuickActionsNext}
                  aria-label={tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.scrollQuickActionsRight')}
                >
                  <IonIcon icon={chevronForwardOutline} />
                </IonButton>
              </div>
            </div>

            {/* KPI metrics row */}
            <div className={styles.metricsRow}>
              {kpis.map(kpi => (
                <div key={kpi.label} className={styles.metricCard}
                  onClick={() => router.push(kpi.route)}
                  role="button" tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && router.push(kpi.route)}
                >
                  <div className={`${styles.metricIconWrap} ${kpi.accent}`}>
                    <IonIcon icon={kpi.icon} className={styles.metricIcon} />
                  </div>
                  <div className={styles.metricBody}>
                    <span className={styles.metricValue}>{kpi.value}</span>
                    <span className={styles.metricLabel}>{kpi.label}</span>
                    <span className={styles.metricHint}>{kpi.hint}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Main 2-column grid: Upcoming Bookings (left) | Recent Activities (right) */}
            <div className={styles.mainGrid}>
              <div className={styles.panel}>
                <div className={styles.panelHeader}>
                  <span className={styles.panelTitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.upcomingBookings')}</span>
                  <span className={styles.panelSubtitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.next7Days')}</span>
                </div>

                <div className={styles.listArea}>
                  {bookingsLoading ? (
                    <div className={styles.emptyState}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.loadingBookings')}</div>
                  ) : displayBookings.length === 0 ? (
                    <div className={styles.emptyState}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.noBookingsScheduledForTheNext')}</div>
                  ) : displayBookings.map(booking => {
                    const dateParts = getDateParts(booking);
                    const bookingRowKey = booking.id || bookingIdentityKey(booking);
                    return (
                      <button
                        key={bookingRowKey}
                        type="button"
                        className={styles.bookingRow}
                        onClick={() => router.push(`/tabs/bookings/${booking.id}`)}
                      >
                        <span className={styles.dateBlock}>
                          <span className={styles.dateDay}>{dateParts.day}</span>
                          <span className={styles.dateMonth}>{dateParts.month}</span>
                        </span>
                        <span className={styles.bookingInfo}>
                          <span className={styles.bookingClientLine}>
                            {renderBookingAvatar(booking)}
                            <span className={styles.bookingClient}>{booking.contact?.name || tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.clientName')}</span>
                          </span>
                          <span
                            className={styles.bookingService}
                            style={{ color: booking.serviceType ? getServiceColor(booking.serviceType as ServiceType) : undefined }}
                          >
                            {booking.serviceType || booking.title || tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.service')}
                          </span>
                          <span className={styles.bookingMeta}>
                            {dateParts.weekday} • {bookingTimeOnly(booking.startTime)} • {formatDuration(booking.duration)}
                          </span>
                          <span className={styles.bookingLocation}>
                            {booking.contact?.address || booking.location || tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.noLocation')}
                          </span>
                          <span className={styles.bookingStaff}>
                            {booking.staff || tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.unassignedStaff')}{booking.resource ? ` • ${booking.resource}` : ''}
                          </span>
                        </span>
                        <span className={styles.bookingRight}>
                          <span className={`${styles.activityBadge} ${styles[bookingBadgeClass(booking.status)]}`}>
                            {formatBookingStatusLabel(booking.status)}
                          </span>
                          <IonIcon icon={chevronForwardOutline} className={styles.rowChevron} />
                        </span>
                      </button>
                    );
                  })}
                </div>

                {bookingsPageCount > 1 && (
                  <div className={styles.pagerRow}>
                    <button
                      type="button"
                      className={styles.pagerButton}
                      disabled={bookingsPage === 0}
                      onClick={() => setBookingsPage(p => Math.max(0, p - 1))}
                      aria-label={tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.previousBookings')}
                    >
                      <IonIcon icon={chevronBackOutline} />
                    </button>
                    <div className={styles.pagerDots}>
                      {Array.from({ length: bookingsPageCount }).map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`${styles.pagerDot} ${idx === bookingsPage ? styles.pagerDotActive : ''}`}
                          onClick={() => setBookingsPage(idx)}
                          aria-label={tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.pageOf', { v1: idx + 1, bookingsPageCount })}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.pagerButton}
                      disabled={bookingsPage >= bookingsPageCount - 1}
                      onClick={() => setBookingsPage(p => Math.min(bookingsPageCount - 1, p + 1))}
                      aria-label={tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.nextBookings')}
                    >
                      <IonIcon icon={chevronForwardOutline} />
                    </button>
                  </div>
                )}

                <IonButton fill="clear" expand="block" className={styles.viewAllButton}
                  onClick={() => router.push('/tabs/bookings')}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.viewAllBookings')}
                  <IonIcon icon={chevronForwardOutline} slot="end" />
                </IonButton>
              </div>

              <div className={styles.panel}>
                <div className={styles.panelHeader}>
                  <span className={styles.panelTitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.recentActivities')}</span>
                </div>

                <div className={styles.listArea}>
                  {activitiesLoading && invoicesLoading ? (
                    <div className={styles.emptyState}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.loadingActivity')}</div>
                  ) : recentActivities.length === 0 ? (
                    <div className={styles.emptyState}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.noRecentActivity')}</div>
                  ) : Object.entries(groupedActivities).map(([categoryName, titleGroups]) => (
                    <div key={categoryName}>
                      <div className={styles.activitySectionLabel}>{categoryName}</div>
                      {Object.entries(titleGroups).map(([groupTitle, group]) => {
                        const isExpanded = Boolean(expandedGroups[groupTitle]);
                        const visibleRows = isExpanded ? group.slice(0, 8) : [group[0]];
                        const moreCount = group.length - 1;
                        const visibleMore = group.slice(1, 4);
                        return (
                          <div key={group[0].id}>
                            {visibleRows.map(activity => (
                              <div key={activity.id} className={styles.activityRow}
                                onClick={() => router.push(activityRoute(activity))}
                                role="button" tabIndex={0}
                                onKeyDown={e => e.key === 'Enter' && router.push(activityRoute(activity))}
                              >
                                <div className={styles.activityIconWrapper}>
                                  <div className={styles.activityTypeCircle}>
                                    <IonIcon icon={getActivityTypeIcon(activity.type)} className={styles.activityTypeIc} />
                                  </div>
                                  <div
                                    className={styles.activityUserBadge}
                                    style={{ background: getBubbleColor(activity.userInitial) }}
                                  >
                                    {activity.userInitial || '?'}
                                  </div>
                                </div>
                                <div className={styles.activityInfo}>
                                  <div className={styles.activityTitle}>{localizeActivity(activity).title}</div>
                                  <div className={styles.activityMeta}>{localizeActivity(activity).detail}</div>
                                </div>
                                <div className={styles.activityRightCol}>
                                  <span className={styles.activityTimestamp}>{localizeActivity(activity).time}</span>
                                  <IonIcon icon={chevronForwardOutline} className={styles.activityChevron} />
                                </div>
                              </div>
                            ))}
                            {moreCount > 0 && (
                              <div className={styles.showMoreRow}
                                onClick={() => toggleGroup(groupTitle)}
                                role="button" tabIndex={0}
                                onKeyDown={e => e.key === 'Enter' && toggleGroup(groupTitle)}
                              >
                                <span className={styles.showMoreLink}>
                                  {isExpanded
                                    ? tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.showLess')
                                    : tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.showMoreLikeThis', { moreCount, v2: translateDynamic(moreCount === 1 ? 'update' : 'updates') })}
                                </span>
                                {!isExpanded && (
                                  <div className={styles.moreAvatarGroup}>
                                    {visibleMore.map((act, idx) => (
                                      <div
                                        key={idx}
                                        className={styles.moreAvatarBubble}
                                        style={{
                                          background: getBubbleColor(act.userInitial),
                                          zIndex: 10 - idx,
                                          marginLeft: idx === 0 ? 0 : -8,
                                        }}
                                      >
                                        {act.userInitial || '?'}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {(overdueInvoices.length > 0 || pendingCount > 0) && (
                  <div className={styles.warningStrip}>
                    <IonIcon icon={warningOutline} />
                    <span>
                      {overdueInvoices.length > 0 && `${overdueInvoices.length} overdue invoice${overdueInvoices.length === 1 ? '' : 's'}`}
                      {overdueInvoices.length > 0 && pendingCount > 0 && ' · '}
                      {pendingCount > 0 && `${pendingCount} pending booking${pendingCount === 1 ? '' : 's'}`}
                    </span>
                  </div>
                )}

                <IonButton fill="clear" expand="block" className={styles.viewAllButton}
                  onClick={() => router.push('/tabs/notifications')}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.viewAllActivity')}
                  <IonIcon icon={chevronForwardOutline} slot="end" />
                </IonButton>
              </div>
            </div>

            {/* Bottom sections: revenue overview & weekly stats */}
            <div className={styles.bottomGrid}>
              {/* Revenue Overview */}
              <div className={styles.panel}>
                <div className={styles.panelHeader}>
                  <span className={styles.panelTitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.revenueOverview')}</span>
                  <span className={styles.panelSubtitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.recentInvoices')}</span>
                </div>
                <div className={styles.revenueBody}>
                  <div className={styles.revenueFigures}>
                    <div className={styles.revenueFigure}>
                      <span className={`${styles.revenueDot} ${styles.dotPaid}`} />
                      <div className={styles.revenueFigureText}>
                        <span className={styles.revenueFigureValue}>{revenueLoading ? '—' : (monthRevenue === null ? '—' : formatCurrency(monthRevenue))}</span>
                        <span className={styles.revenueFigureLabel}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.collectedThisMonth')}</span>
                      </div>
                    </div>
                    <div className={styles.revenueFigure}>
                      <span className={`${styles.revenueDot} ${styles.dotDue}`} />
                      <div className={styles.revenueFigureText}>
                        <span className={styles.revenueFigureValue}>{invoicesLoading ? '—' : formatCurrency(outstandingAmount)}</span>
                        <span className={styles.revenueFigureLabel}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.outstanding')}</span>
                      </div>
                    </div>
                    <div className={styles.revenueFigure}>
                      <span className={`${styles.revenueDot} ${styles.dotOverdue}`} />
                      <div className={styles.revenueFigureText}>
                        <span className={styles.revenueFigureValue}>{invoicesLoading ? '—' : formatCurrency(overdueAmount)}</span>
                        <span className={styles.revenueFigureLabel}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.overdue')}</span>
                      </div>
                    </div>
                  </div>
                  {revenueSplit && (
                    <div className={styles.revenueBar}>
                      <span className={styles.barPaid} style={{ width: `${revenueSplit.paid}%` }} />
                      <span className={styles.barDue} style={{ width: `${revenueSplit.due}%` }} />
                      <span className={styles.barOverdue} style={{ width: `${revenueSplit.overdue}%` }} />
                    </div>
                  )}
                </div>
                <IonButton fill="clear" expand="block" className={styles.viewAllButton}
                  onClick={() => router.push('/tabs/billing')}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.goToBilling')}
                  <IonIcon icon={chevronForwardOutline} slot="end" />
                </IonButton>
              </div>

              {/* Weekly stats */}
              <div className={styles.panel}>
                <div className={styles.panelHeader}>
                  <span className={styles.panelTitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.thisWeek')}</span>
                  <span className={styles.panelSubtitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.atAGlance')}</span>
                </div>
                <div className={styles.statsList}>
                  {weekStats.map(stat => (
                    <div key={stat.label} className={styles.statsRow}>
                      <span className={styles.statsLabel}>{stat.label}</span>
                      <span className={styles.statsValue}>{stat.value}</span>
                    </div>
                  ))}
                </div>
                <IonButton fill="clear" expand="block" className={styles.viewAllButton}
                  onClick={() => router.push('/tabs/reports')}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.viewReports')}
                  <IonIcon icon={chevronForwardOutline} slot="end" />
                </IonButton>
              </div>
            </div>

          </div>
        </div>
      </IonContent>

      <div
        className={`${styles.shortcutEditorBackdrop} ${isEditMode ? styles.shortcutEditorBackdropOpen : ''}`}
        onClick={closeShortcutEditor}
        aria-hidden={!isEditMode}
      />

      <aside
        className={`${styles.shortcutEditorPanel} ${isEditMode ? styles.shortcutEditorPanelOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.editQuickAccessShortcuts')}
      >
        <div className={styles.shortcutEditorHeader}>
          <div>
            <h3 className={styles.shortcutEditorTitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.editQuickAccess')}</h3>
            <p className={styles.shortcutEditorSubtitle}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.chooseWhatAppearsInYourDesktop')}</p>
          </div>
          <IonButton fill="clear" className={styles.shortcutEditorClose} onClick={closeShortcutEditor}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        </div>

        <div className={styles.shortcutEditorBody}>
          {categorizedShortcuts.map((category) => (
            <section key={category.id} className={styles.shortcutEditorSection}>
              <h4 className={styles.shortcutEditorSectionTitle}>{category.label}</h4>
              <div className={styles.shortcutEditorList}>
                {category.shortcuts.map((shortcut) => {
                  const isActive = draftShortcutIds.includes(shortcut.id);
                  return (
                    <button
                      key={shortcut.id}
                      type="button"
                      className={`${styles.shortcutEditorItem} ${isActive ? styles.shortcutEditorItemActive : ''}`}
                      onClick={() => toggleDraftShortcut(shortcut.id)}
                    >
                      <span className={styles.shortcutEditorItemStart}>
                        <IonIcon icon={shortcut.icon} className={styles.shortcutEditorIcon} />
                        <span className={styles.shortcutEditorItemLabel}>{shortcut.title}</span>
                      </span>
                      <span className={`${styles.shortcutEditorCheck} ${isActive ? styles.shortcutEditorCheckOn : ''}`}>
                        {isActive && <IonIcon icon={checkmarkOutline} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className={styles.shortcutEditorFooter}>
          <IonButton fill="outline" className={styles.shortcutEditorCancel} onClick={closeShortcutEditor} disabled={isSavingShortcuts}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.cancel')}
          </IonButton>
          <IonButton fill="solid" className={styles.shortcutEditorSave} onClick={saveShortcutEditor} disabled={isSavingShortcuts}>
            {isSavingShortcuts ? <IonSpinner name="dots" /> : tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.saveChanges')}
          </IonButton>
        </div>
      </aside>

      {showCreateBooking && (
        <CreateBookingDesktop onClose={() => setShowCreateBooking(false)} />
      )}
    </IonPage>
  );
};

export default memo(DashboardOwnersDesktop);
